import { NotFound } from '@pages/NotFound';
import { lazy, Suspense } from 'react';
import { Navigate, type RouteObject } from 'react-router';

const asyncComponent = (importFunc: () => Promise<{ default: React.ComponentType }>) => {
  const Component = lazy(importFunc);
  return (
    <Suspense fallback={<div className="min-h-screen bg-background" />}>
      <Component />
    </Suspense>
  );
};

const rootPageElement = asyncComponent(() => import('@pages/RootPage'));

const routes: RouteObject[] = [
  {
    path: '/login/*',
    element: asyncComponent(() => import('@pages/Auth/LoginPage')),
  },
  {
    path: '/student/login',
    element: asyncComponent(() => import('@pages/Student/StudentLoginPage')),
  },
  {
    path: '/student',
    element: asyncComponent(() => import('@pages/Student/StudentRootPage')),
    errorElement: <NotFound />,
    children: [
      {
        index: true,
        element: asyncComponent(() => import('@pages/Student/ExamBookingPage')),
      },
    ],
  },
  {
    path: '/trainer/login',
    element: asyncComponent(() => import('@pages/TrainerPortal/TrainerLoginPage')),
  },
  {
    path: '/trainer',
    element: asyncComponent(() => import('@pages/TrainerPortal/TrainerRootPage')),
    errorElement: <NotFound />,
    children: [
      {
        index: true,
        element: asyncComponent(() => import('@pages/TrainerPortal/TrainerSchedulePage')),
      },
      {
        path: 'classes/:classId',
        element: asyncComponent(() => import('@pages/TrainerPortal/TrainerClassSessionPage')),
      },
      {
        path: 'classes/:classId/students/:classClientId/confirm',
        element: asyncComponent(() => import('@pages/TrainerPortal/TrainerConfirmDocumentsPage')),
      },
      {
        path: 'classes/:classId/students/:classClientId/workflow-review',
        element: asyncComponent(() => import('@pages/TrainerPortal/TrainerWorkflowReviewPage')),
      },
      {
        path: 'classes/:classId/students/:classClientId/eoi-trainer',
        element: asyncComponent(() => import('@pages/TrainerPortal/TrainerEoiTrainerPage')),
      },
    ],
  },
  {
    path: '/permissionDenied',
    element: asyncComponent(() => import('@pages/PermissionPage')),
  },
  {
    path: '/pub/*',
    element: asyncComponent(() => import('@pages/PublicRootPage')),
    errorElement: <NotFound />,
    children: [
      {
        path: 'class-check-in/completed',
        element: asyncComponent(() => import('@pages/Class/PublicStudentCheckInCompletedPage')),
      },
      {
        path: 'class-check-in/phone/completed',
        element: asyncComponent(() => import('@pages/Class/PublicStudentCheckInCompletedPage')),
      },
      {
        path: 'class-check-in/phone/:token',
        element: asyncComponent(() => import('@pages/Class/PublicStudentPhoneCheckInPage')),
      },
      {
        path: 'class-check-in/:token',
        element: asyncComponent(() => import('@pages/Class/PublicStudentCheckInPage')),
      },
      {
        path: 'class-post-training/completed',
        element: asyncComponent(
          () => import('@pages/Class/PublicStudentPostTrainingCompletedPage'),
        ),
      },
      {
        path: 'class-post-training/:token',
        element: asyncComponent(() => import('@pages/Class/PublicStudentPostTrainingPage')),
      },
      {
        path: 'clientDeclaration',
        element: asyncComponent(() => import('@pages/ClientDeclaration/ClientDeclarationPage')),
      },
      {
        path: 'documentShares/:shareCode/browser',
        element: asyncComponent(
          () => import('@pages/DocumentShare/PublicDocumentShareBrowserPage'),
        ),
      },
      {
        path: 'documentShares/:shareCode',
        element: asyncComponent(
          () => import('@pages/DocumentShare/PublicDocumentSharePasswordPage'),
        ),
      },
    ],
  },
  {
    path: '/',
    element: rootPageElement,
    errorElement: <NotFound />,
    handle: { requiresAuth: true },
    children: [
      { index: true, element: asyncComponent(() => import('@pages/Dashboard/DashboardPage')) },
      {
        path: 'adminConsultantBoard/*',
        element: asyncComponent(() => import('@pages/Dashboard/AdminConsultantBoardPage')),
      },
      {
        path: 'organizationBoard/*',
        element: asyncComponent(() => import('@pages/Dashboard/OrganizationDashboardPage')),
      },
      {
        path: 'courses/*',
        element: asyncComponent(() => import('@pages/Courses/CoursesPage')),
        handle: { breadcrumb: 'Courses' },
      },
      {
        path: 'suppliers/*',
        element: <Navigate to="/prices" replace />,
      },
      {
        path: 'users/*',
        element: asyncComponent(() => import('@pages/User/UserPage')),
        handle: { breadcrumb: 'Users' },
      },
      {
        path: 'clients/*',
        element: asyncComponent(() => import('@pages/Client/ClientPage')),
        handle: { breadcrumb: 'Clients' },
      },
      {
        path: 'clients/:clientId/certificate-export',
        element: asyncComponent(() => import('@pages/Client/ClientCertificateExportPage')),
        handle: { breadcrumb: 'Certificate Export' },
      },
      {
        path: 'studentAccounts/*',
        element: asyncComponent(() => import('@pages/StudentAccount/StudentAccountPage')),
        handle: { breadcrumb: 'Student Accounts' },
      },
      {
        path: 'orders/*',
        element: asyncComponent(() => import('@pages/Orders/OrderPage')),
      },
      {
        path: 'receivables/*',
        element: asyncComponent(() => import('@pages/Accounts/Receivable/ReceivablePage')),
      },
      {
        path: 'cash-daily-closes',
        element: asyncComponent(() => import('@pages/Accounts/CashDailyClosePage')),
      },
      {
        path: 'payables/*',
        element: asyncComponent(() => import('@pages/Accounts/Payable/PayablePage')),
      },
      {
        path: 'commissions/*',
        element: asyncComponent(() => import('@pages/Accounts/Commission/CommissionPage')),
      },
      {
        path: 'prices/sales',
        element: asyncComponent(() => import('@pages/Price/SalesPricePage')),
      },
      {
        path: 'prices/*',
        element: asyncComponent(() => import('@pages/Price/PricePage')),
      },
      {
        path: 'agencies/*',
        element: asyncComponent(() => import('@pages/Agency/AgencyPage')),
      },
      {
        path: 'trainers/*',
        element: asyncComponent(() => import('@pages/Trainer/TrainerPage')),
      },
      {
        path: 'expenseTypes/*',
        element: asyncComponent(() => import('@pages/Expenses/ExpensesTypes/ExpensesTypesPage')),
      },
      {
        path: 'expenses/*',
        element: asyncComponent(() => import('@pages/Expenses/ExpensesPage')),
      },
      {
        path: 'classes/*',
        element: asyncComponent(() => import('@pages/Class/ClassPage')),
      },
      {
        path: 'exams/*',
        element: asyncComponent(() => import('@pages/Exam/ExamPage')),
      },
      {
        path: 'organizations/*',
        element: asyncComponent(() => import('@pages/Organization/OrganizationPage')),
      },
      {
        path: 'permission/*',
        element: asyncComponent(() => import('@pages/Permission/PermissionManagePage')),
      },
      {
        path: 'organizations/users/*',
        element: asyncComponent(() => import('@pages/OrganizationUser/OrganizationUserPage')),
      },
      {
        path: 'invoices/*',
        element: asyncComponent(() => import('@pages/Accounts/Invoice/InvoicePage')),
      },
    ],
  },
  {
    path: 'organizations/',
    element: rootPageElement,
    errorElement: <NotFound />,
    handle: { requiresAuth: true },
    children: [
      {
        index: true,
        element: asyncComponent(() => import('@pages/Organization/OrganizationPage')),
      },
      {
        path: 'users',
        element: asyncComponent(() => import('@pages/OrganizationUser/OrganizationUserPage')),
      },
      {
        path: 'clients',
        element: asyncComponent(() => import('@pages/Client/OrganizationClientPage')),
      },
    ],
  },
  {
    path: 'settings/',
    element: rootPageElement,
    errorElement: <NotFound />,
    handle: { requiresAuth: true },
    children: [
      {
        index: true,
        element: asyncComponent(() => import('@pages/Setting/SettingPage')),
      },
    ],
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

export default routes;
